import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, GripVertical, Timer } from "lucide-react";
import parseIngredientLines from "@/lib/parseIngredients";
import { cardExcerpt } from "@/lib/stripMarkdown";

/**
 * Structured recipe builder with big, mobile-friendly tap targets.
 * Sharers add ingredients and steps one line at a time; the component serialises
 * back to markdown so it plugs into the existing context field (no schema change).
 */
export default function RecipeBuilder({ notes, ingredients, steps, stepTimers, onChange }) {
  const timers = stepTimers && stepTimers.length === steps.length ? stepTimers : steps.map(() => "");
  const set = (patch) => onChange({ notes, ingredients, steps, stepTimers: timers, ...patch });

  const addIngredient = () => set({ ingredients: [...ingredients, ""] });
  const setIngredient = (i, v) => set({ ingredients: ingredients.map((x, idx) => (idx === i ? v : x)) });
  const removeIngredient = (i) => {
    if (ingredients.length === 1) { set({ ingredients: [""] }); return; }
    set({ ingredients: ingredients.filter((_, idx) => idx !== i) });
  };

  // Paste-a-list: same shared parser the edit panel + backend use. Because
  // this planter stores ingredients as flat strings (not {qty,unit,name}),
  // we re-serialise each parsed row back into a single line so what the
  // user sees below the paste UI is exactly what will be saved.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteMode, setPasteMode] = useState("replace");
  const applyPaste = () => {
    const parsed = parseIngredientLines(pasteText);
    if (parsed.length === 0) return;
    const lines = parsed.map((r) => [r.qty, r.unit, r.name].filter(Boolean).join(" "));
    const cleanExisting = ingredients.filter((s) => (s || "").trim());
    const next = pasteMode === "replace" ? lines : [...cleanExisting, ...lines];
    set({ ingredients: next });
    setPasteOpen(false);
    setPasteText("");
  };

  // Steps paste-a-list — cooks often keep an entire recipe in a note app
  // and pasting the full block is faster than tap-add-tap-add. We just
  // split on newlines/blank-line-blocks and strip common numbering ("1.",
  // "Step 1:", "•"). Timers aren't imported (they're per-step and users
  // add those from the row after).
  const [stepPasteOpen, setStepPasteOpen] = useState(false);
  const [stepPasteText, setStepPasteText] = useState("");
  const [stepPasteMode, setStepPasteMode] = useState("replace");
  const applyStepPaste = () => {
    const raw = String(stepPasteText || "");
    // Prefer double-newline blocks so multi-line steps stay together;
    // fall back to single-newline splitting when the paste has no blanks.
    const blocks = raw.includes("\n\n") ? raw.split(/\n{2,}/) : raw.split(/\r?\n/);
    const cleaned = blocks
      .map((b) =>
        b
          .replace(/^\s*(?:step\s*\d+[:.\-]?\s+|\d+[.)]\s+|[-*+•]\s+)/i, "")
          .replace(/\s+$/g, "")
          .trim(),
      )
      .filter(Boolean);
    if (cleaned.length === 0) return;
    const existing = (steps || []).filter((s) => (s || "").trim());
    const nextSteps = stepPasteMode === "replace" ? cleaned : [...existing, ...cleaned];
    // Preserve existing timers when appending; blank timers for new steps.
    const nextTimers = stepPasteMode === "replace"
      ? nextSteps.map(() => "")
      : [...(stepTimers || []).slice(0, existing.length), ...cleaned.map(() => "")];
    set({ steps: nextSteps, stepTimers: nextTimers });
    setStepPasteOpen(false);
    setStepPasteText("");
  };

  const addStep = () => set({ steps: [...steps, ""], stepTimers: [...timers, ""] });
  const setStep = (i, v) => set({ steps: steps.map((x, idx) => (idx === i ? v : x)) });
  const setStepTimer = (i, v) => set({ stepTimers: timers.map((x, idx) => (idx === i ? v : x)) });
  const removeStep = (i) => {
    if (steps.length === 1) { set({ steps: [""], stepTimers: [""] }); return; }
    set({ steps: steps.filter((_, idx) => idx !== i), stepTimers: timers.filter((_, idx) => idx !== i) });
  };

  const onIngKey = (e, i) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const next = [...ingredients];
      next.splice(i + 1, 0, "");
      set({ ingredients: next });
      setTimeout(() => document.getElementById(`ing-${i + 1}`)?.focus(), 0);
    }
  };
  const onStepKey = (e, i) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const next = [...steps];
      next.splice(i + 1, 0, "");
      set({ steps: next });
      setTimeout(() => document.getElementById(`step-${i + 1}`)?.focus(), 0);
    }
  };

  return (
    <div className="rounded-2xl border border-[#B79256]/40 bg-[#FBF8F1] p-4 sm:p-6 space-y-8" data-testid="recipe-builder">
      {/* Story (was "notes") — this is the top of the recipe AND the text
          Willoah pulls for the feed-card excerpt. Renamed + copy rewritten
          so authors treat it as the story they'd tell a friend, not an
          optional footnote. */}
      <div data-testid="recipe-story-block">
        <div className="accent-label text-[#B79256] mb-1">
          Your story <span className="text-[#2A2823]/50 font-normal ml-1">· 1–3 sentences</span>
        </div>
        <p className="text-xs text-[#2A2823]/60 mb-2 italic">
          Write this at the top — it becomes the excerpt on the feed card, and it's what a friend actually reads before they scroll to your ingredients.
        </p>
        <Textarea
          data-testid="recipe-notes"
          value={notes}
          onChange={(e) => set({ notes: e.target.value })}
          rows={3}
          placeholder="e.g. Fast weeknight dinner everyone eats. My mom's version, with the lemon zest added at the end."
          className="rounded-xl border-[#B79256]/40 bg-white/70 focus-visible:ring-[#4E5A3D]"
        />
        {/* Live card-excerpt preview — the same picker the feed uses so
            authors see exactly what will appear on their card. If empty
            or all-scaffolding, show a gentle nudge instead. */}
        {(() => {
          const preview = cardExcerpt({ context: notes });
          return preview ? (
            <div
              data-testid="recipe-story-preview"
              className="mt-3 rounded-xl bg-white/80 border border-[#B79256]/25 px-3 py-2 text-xs"
            >
              <div className="accent-label text-[10px] text-[#B79256] mb-1">On the feed card</div>
              <p className="text-[#2A2823]/75 italic line-clamp-2 leading-snug">{preview}</p>
            </div>
          ) : (
            <div
              data-testid="recipe-story-empty-hint"
              className="mt-3 rounded-xl bg-[#B79256]/8 border border-dashed border-[#B79256]/40 px-3 py-2 text-xs text-[#2A2823]/60 italic"
            >
              Add a line here so your card doesn't show a bare ingredient list. Even one sentence works.
            </div>
          );
        })()}
      </div>

      {/* Visual divider — signals "story above, structured recipe below".
          Copy explicitly tells authors these two sections show up in
          DIFFERENT places (excerpt vs. full recipe view) so they don't
          accidentally paste their story into Ingredients. */}
      <div className="relative py-1" data-testid="recipe-scaffolding-divider">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-[#B79256]/30 border-dashed" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-[#FBF8F1] px-3 accent-label text-[10px] text-[#B79256]/85 tracking-widest">
            what you need &amp; how — shown inside the recipe
          </span>
        </div>
      </div>

      {/* Ingredients — big, one line each, tap-friendly */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="accent-label text-[#B79256]">ingredients</div>
            <p className="text-xs text-[#2A2823]/55 mt-1">One per line. Hit Enter or the big + to add another.</p>
          </div>
          <span className="accent-label text-[#2A2823]/50 text-xs">{ingredients.filter(Boolean).length} added</span>
        </div>

        <ul className="space-y-2" data-testid="ingredients-list">
          {ingredients.map((val, i) => (
            <li key={i} className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-[#B79256]/60 hidden sm:block" strokeWidth={1.75} />
              <Input
                id={`ing-${i}`}
                data-testid={`ing-${i}`}
                value={val}
                onChange={(e) => setIngredient(i, e.target.value)}
                onKeyDown={(e) => onIngKey(e, i)}
                placeholder={i === 0 ? "e.g. 3 cups all-purpose flour" : "add ingredient…"}
                className="h-12 sm:h-11 rounded-xl border-[#B79256]/40 bg-white focus-visible:ring-[#4E5A3D] text-base sm:text-sm"
                inputMode="text"
                autoComplete="off"
              />
              <button
                type="button"
                data-testid={`ing-remove-${i}`}
                onClick={() => removeIngredient(i)}
                aria-label="Remove ingredient"
                className="p-3 rounded-full text-[#2A2823]/50 hover:text-[#4E5A3D] hover:bg-[#B79256]/12 transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </li>
          ))}
        </ul>

        <button
          type="button"
          data-testid="add-ingredient-btn"
          onClick={addIngredient}
          className="mt-4 w-full h-14 rounded-2xl bg-[#4E5A3D]/8 border-2 border-dashed border-[#4E5A3D]/40 text-[#4E5A3D] hover:bg-[#4E5A3D]/12 transition-colors flex items-center justify-center gap-2 font-medium touch-manipulation active:scale-[0.98]"
        >
          <Plus className="w-5 h-5" strokeWidth={2} />
          Add ingredient
        </button>

        <button
          type="button"
          data-testid="paste-ingredients-toggle"
          onClick={() => setPasteOpen((o) => !o)}
          className="mt-2 text-sm text-[#B79256] hover:text-[#4E5A3D] underline underline-offset-2"
        >
          {pasteOpen ? "Cancel paste" : "…or paste a whole list"}
        </button>

        {pasteOpen && (
          <div
            className="mt-3 rounded-2xl border border-[#B79256]/40 bg-white p-3 sm:p-4 space-y-2"
            data-testid="paste-ingredients-panel"
          >
            <div className="accent-label text-[#B79256] text-[10px]">Paste ingredients — one per line</div>
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              data-testid="paste-ingredients-textarea"
              rows={6}
              autoFocus
              placeholder={"e.g.\n8 eggs\n1/2 cup heavy whipping cream\n1/2 lb sausage\nVegetables (peppers or spinach)\nParmesan or cheddar cheese"}
              className="rounded-xl border-[#B79256]/40 bg-[#FBF8F1] font-mono text-sm whitespace-pre-wrap"
            />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1 text-[#2A2823]/70">
                  <input
                    type="radio"
                    name="rb-paste-mode"
                    value="replace"
                    checked={pasteMode === "replace"}
                    onChange={() => setPasteMode("replace")}
                    data-testid="paste-ingredients-mode-replace"
                  />
                  Replace list
                </label>
                <label className="inline-flex items-center gap-1 text-[#2A2823]/70">
                  <input
                    type="radio"
                    name="rb-paste-mode"
                    value="append"
                    checked={pasteMode === "append"}
                    onChange={() => setPasteMode("append")}
                    data-testid="paste-ingredients-mode-append"
                  />
                  Add to list
                </label>
              </div>
              <button
                type="button"
                onClick={applyPaste}
                disabled={!pasteText.trim()}
                data-testid="paste-ingredients-apply"
                className="inline-flex items-center gap-1 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] disabled:opacity-40 px-4 h-9 text-sm"
              >
                Import
              </button>
            </div>
            <p className="text-[11px] text-[#2A2823]/55">
              Each line becomes its own row — tweak quantities after importing if needed.
            </p>
          </div>
        )}
      </section>

      {/* Steps — numbered, one at a time */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="accent-label text-[#B79256]">steps</div>
            <p className="text-xs text-[#2A2823]/55 mt-1">Numbered, one instruction per step.</p>
          </div>
          <span className="accent-label text-[#2A2823]/50 text-xs">{steps.filter(Boolean).length} added</span>
        </div>

        <ol className="space-y-2" data-testid="steps-list">
          {steps.map((val, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-3 sm:mt-2.5 accent-label text-[#B79256] w-6 flex-shrink-0 text-right">{i + 1}.</span>
              <div className="flex-1 space-y-2">
                <Textarea
                  id={`step-${i}`}
                  data-testid={`step-${i}`}
                  value={val}
                  onChange={(e) => setStep(i, e.target.value)}
                  onKeyDown={(e) => onStepKey(e, i)}
                  placeholder={i === 0 ? "e.g. Whisk dry ingredients in a large bowl." : "next step…"}
                  rows={2}
                  className="rounded-xl border-[#B79256]/40 bg-white focus-visible:ring-[#4E5A3D] text-base sm:text-sm min-h-[3rem]"
                />
                <div className="flex items-center gap-2">
                  <Timer className="w-4 h-4 text-[#B79256]/70" strokeWidth={1.75} />
                  <Input
                    data-testid={`step-timer-${i}`}
                    value={timers[i] || ""}
                    onChange={(e) => setStepTimer(i, e.target.value)}
                    placeholder="Optional timer — e.g. 5m, 90s, 1h 30m"
                    className="h-10 rounded-full border-[#B79256]/40 bg-white focus-visible:ring-[#4E5A3D] text-sm max-w-xs"
                    autoComplete="off"
                  />
                </div>
              </div>
              <button
                type="button"
                data-testid={`step-remove-${i}`}
                onClick={() => removeStep(i)}
                aria-label="Remove step"
                className="mt-2 p-3 rounded-full text-[#2A2823]/50 hover:text-[#4E5A3D] hover:bg-[#B79256]/12 transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </li>
          ))}
        </ol>

        <button
          type="button"
          data-testid="add-step-btn"
          onClick={addStep}
          className="mt-4 w-full h-14 rounded-2xl bg-[#4E5A3D]/8 border-2 border-dashed border-[#4E5A3D]/40 text-[#4E5A3D] hover:bg-[#4E5A3D]/12 transition-colors flex items-center justify-center gap-2 font-medium touch-manipulation active:scale-[0.98]"
        >
          <Plus className="w-5 h-5" strokeWidth={2} />
          Add step
        </button>

        <button
          type="button"
          data-testid="paste-steps-toggle"
          onClick={() => setStepPasteOpen((o) => !o)}
          className="mt-2 text-sm text-[#B79256] hover:text-[#4E5A3D] underline underline-offset-2"
        >
          {stepPasteOpen ? "Cancel paste" : "…or paste the whole method"}
        </button>

        {stepPasteOpen && (
          <div
            className="mt-3 rounded-2xl border border-[#B79256]/40 bg-white p-3 sm:p-4 space-y-2"
            data-testid="paste-steps-panel"
          >
            <div className="accent-label text-[#B79256] text-[10px]">Paste steps — one per line or paragraph</div>
            <Textarea
              value={stepPasteText}
              onChange={(e) => setStepPasteText(e.target.value)}
              data-testid="paste-steps-textarea"
              rows={7}
              autoFocus
              placeholder={"e.g.\n1. Whisk the eggs and cream in a bowl.\n2. Brown the sausage in a skillet.\n3. Pour eggs over the sausage and top with cheese.\n4. Bake at 350°F for 20–25 minutes."}
              className="rounded-xl border-[#B79256]/40 bg-[#FBF8F1] font-mono text-sm whitespace-pre-wrap"
            />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1 text-[#2A2823]/70">
                  <input
                    type="radio"
                    name="rb-steps-paste-mode"
                    value="replace"
                    checked={stepPasteMode === "replace"}
                    onChange={() => setStepPasteMode("replace")}
                    data-testid="paste-steps-mode-replace"
                  />
                  Replace method
                </label>
                <label className="inline-flex items-center gap-1 text-[#2A2823]/70">
                  <input
                    type="radio"
                    name="rb-steps-paste-mode"
                    value="append"
                    checked={stepPasteMode === "append"}
                    onChange={() => setStepPasteMode("append")}
                    data-testid="paste-steps-mode-append"
                  />
                  Add to method
                </label>
              </div>
              <button
                type="button"
                onClick={applyStepPaste}
                disabled={!stepPasteText.trim()}
                data-testid="paste-steps-apply"
                className="inline-flex items-center gap-1 rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] disabled:opacity-40 px-4 h-9 text-sm"
              >
                Import
              </button>
            </div>
            <p className="text-[11px] text-[#2A2823]/55">
              Numbering like "1.", "Step 1:" is stripped automatically — add timers per row after importing.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

/** Serialise the structured builder state into a single markdown context string. */
export function buildRecipeMarkdown({ notes, ingredients, steps, stepTimers = [] }) {
  const parts = [];
  if (notes && notes.trim()) parts.push(notes.trim());
  const cleanIng = (ingredients || []).map((s) => s.trim()).filter(Boolean);
  if (cleanIng.length) parts.push(`## Ingredients\n\n${cleanIng.map((x) => `- ${x}`).join("\n")}`);
  const stepLines = [];
  for (let i = 0, n = 0; i < (steps || []).length; i += 1) {
    const text = (steps[i] || "").trim();
    if (!text) continue;
    n += 1;
    const t = (stepTimers[i] || "").trim();
    stepLines.push(`${n}. ${text}${t ? ` [timer: ${t}]` : ""}`);
  }
  if (stepLines.length) parts.push(`## Steps\n\n${stepLines.join("\n")}`);
  return parts.join("\n\n");
}
